import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import withRouter from "../../../components/withRouter";
import { authClient } from "../../../lib/auth-client";
import { toast } from "react-toastify";
import { Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldDescription } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";

const ResetPassw = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [confirmPasswordShow, setConfirmPasswordShow] =
    useState<boolean>(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "A senha deve ter pelo menos 8 caracteres")
        .matches(RegExp("(.*[a-z].*)"), "Pelo menos letra minúscula")
        .matches(RegExp("(.*[A-Z].*)"), "Pelo menos letra maiúscula")
        .matches(RegExp("(.*[0-9].*)"), "Pelo menos um número")
        .required("Este campo é obrigatório"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), ""], "As senhas não coincidem")
        .required("Confirmar senha é obrigatório"),
    }),
    onSubmit: async (values: { password: string; confirm_password: string }) => {
      setLoader(true);
      const token = searchParams.get("token");

      if (token) {
        try {
          const { error } = await authClient.resetPassword({
            newPassword: values.password,
            token,
          });

          if (error) {
            toast.error(error.message || "Erro ao redefinir senha");
          } else {
            validation.resetForm();
            toast.success("Senha redefinida com sucesso!");
            navigate("/login");
          }
        } catch {
          toast.error("Erro ao redefinir senha");
        } finally {
          setLoader(false);
        }
      } else {
        toast.error("Token inválido");
        setLoader(false);
      }
    },
  });

  const password = validation.values.password;
  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);

  document.title = "Criar nova senha | Ad Pulse";

  return (
    <React.Fragment>
      <div className="relative isolate min-h-screen bg-white text-gray-900">
        {/* Main content */}
        <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
            <CardHeader className="space-y-1 pb-4 pt-7 text-center">
              <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                Criar nova senha
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Sua nova senha deve ser diferente da senha usada
                anteriormente.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 sm:text-sm">
                Defina uma senha forte para proteger melhor a sua conta.
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  validation.handleSubmit();
                }}
                className="space-y-4"
              >
                {/* Password */}
                <div>
                  <label
                    htmlFor="password-input"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <Input
                      type={passwordShow ? "text" : "password"}
                      placeholder="Digite a senha"
                      id="password-input"
                      name="password"
                      value={validation.values.password}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pr-11 text-sm text-gray-700 placeholder:text-gray-400 ${
                        validation.errors.password && validation.touched.password
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 border-0 bg-transparent !px-0 !py-0 text-gray-400 shadow-none hover:bg-transparent hover:text-gray-600 focus-visible:outline-none focus-visible:ring-0"
                      onClick={() => setPasswordShow(!passwordShow)}
                      tabIndex={-1}
                      aria-label={passwordShow ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {passwordShow ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {validation.errors.password && validation.touched.password ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <X className="h-3.5 w-3.5 shrink-0" />
                      {validation.errors.password}
                    </FieldDescription>
                  ) : (
                    <FieldDescription className="mt-1 text-xs text-gray-500">
                      Deve ter pelo menos 8 caracteres.
                    </FieldDescription>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label
                    htmlFor="confirm-password-input"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    Confirme sua senha
                  </label>
                  <div className="relative">
                    <Input
                      type={confirmPasswordShow ? "text" : "password"}
                      placeholder="Confirme a senha"
                      id="confirm-password-input"
                      name="confirm_password"
                      value={validation.values.confirm_password}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pr-11 text-sm text-gray-700 placeholder:text-gray-400 ${
                        validation.errors.confirm_password &&
                        validation.touched.confirm_password
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 border-0 bg-transparent !px-0 !py-0 text-gray-400 shadow-none hover:bg-transparent hover:text-gray-600 focus-visible:outline-none focus-visible:ring-0"
                      onClick={() =>
                        setConfirmPasswordShow(!confirmPasswordShow)
                      }
                      tabIndex={-1}
                      aria-label={
                        confirmPasswordShow
                          ? "Ocultar senha"
                          : "Mostrar senha"
                      }
                    >
                      {confirmPasswordShow ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {validation.errors.confirm_password &&
                  validation.touched.confirm_password ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <X className="h-3.5 w-3.5 shrink-0" />
                      {validation.errors.confirm_password}
                    </FieldDescription>
                  ) : null}
                </div>

                {/* Password rules */}
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p className="mb-2 text-sm font-medium text-gray-700">
                    A senha deve conter:
                  </p>
                  <div className="grid gap-1.5">
                    <PasswordRule
                      passed={hasMinLength}
                      label={
                        <>
                          Mínimo de <strong>8 caracteres</strong>
                        </>
                      }
                    />
                    <PasswordRule
                      passed={hasLowercase}
                      label={
                        <>
                          Uma letra <strong>minúscula</strong> (a-z)
                        </>
                      }
                    />
                    <PasswordRule
                      passed={hasUppercase}
                      label={
                        <>
                          Uma letra <strong>maiúscula</strong> (A-Z)
                        </>
                      }
                    />
                    <PasswordRule
                      passed={hasNumber}
                      label={
                        <>
                          Um <strong>número</strong> (0-9)
                        </>
                      }
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loader}
                  className="h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                >
                  {loader ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Redefinir senha
                </Button>
              </form>

              <div className="pt-1 text-center">
                <p className="text-xs text-gray-500 sm:text-sm">
                  Lembrou da sua senha?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-red-600 underline underline-offset-4 hover:text-red-700"
                  >
                    Voltar para o login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <footer className="pb-4 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
          <span className="text-red-500">&#10084;</span>
        </footer>
      </div>
    </React.Fragment>
  );
};

function PasswordRule({
  passed,
  label,
}: {
  passed: boolean;
  label: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {passed ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <X className="h-3.5 w-3.5 text-gray-400" />
      )}
      <span className={passed ? "text-green-600" : "text-gray-500"}>
        {label}
      </span>
    </div>
  );
}

export default withRouter(ResetPassw);
